import React, { useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
    FormGroup,
    Box,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Typography,
    Grid,
    Divider,
    Switch,
    IconButton,
    InputAdornment,
} from '@mui/material';
import {
    EmailOutlined,
    AttachFile,
    Lock,
    Mail,
    Phone,
} from '@mui/icons-material';

import { useForm } from 'react-hook-form';
import * as Sentry from '@sentry/react';
import DialogWithHeader from '../../foundation/DialogWithHeader';
import CustomBR from '../../foundation/CustomBR';
import {
    LoggedInUser,
    UserProfileViewModel,
} from '../../../../types/auth.types';
import {
    JobSearchResultViewModel,
    JobViewModel,
} from '../../../../types/jobs.types';
import Anchor from '../../foundation/Anchor';
import Select from '../../foundation/Select';
import TextArea from '../../foundation/TextArea';
import Checkbox from '../../foundation/Checkbox';
import { registerUser } from '../../../api/auth.api';
import TextField from '../../foundation/TextField';
import { emailPattern } from '../../../utils/forms.utils';
import {
    getUserSettings,
    postUserProfile,
    putUserSettings,
    uploadUserCV,
} from '../../../api/user.api';
import Alert from '../../foundation/Alert';

export type ApplyToJobMissingDataModalProps = {
    open?: boolean;
    onClose: () => void;
    job?: JobViewModel;
    user?: LoggedInUser;
    userProfile?: UserProfileViewModel;
    onRegistrationComplete: (user: LoggedInUser) => void;
    onUserProfileUpdate: (user: UserProfileViewModel) => void;
    onApplyClick: (
        userProfile: UserProfileViewModel,
        job: JobViewModel,
        data: { message: string; cvid?: number }
    ) => void;
};

const ApplyToJobMissingDataModalStyle = styled('div')``;

interface IFormRegister {
    email: string;
    password: string;
    smartAgentByActivity: boolean;
    firstname: string;
    lastname: string;
    phone: string;
    cv?: FileList;
    user?: LoggedInUser;
    job?: JobSearchResultViewModel;
    message?: string;
}

const emptyFormDefaults = (): Partial<IFormRegister> => ({
    email: '',
    password: '',
    smartAgentByActivity: true,
    firstname: '',
    lastname: '',
    phone: '',
    message: '',
});

const ApplyToJobMissingDataModal = (props: ApplyToJobMissingDataModalProps) => {
    const [formErr, setFormErr] = useState<string>(null);
    const [settingsShowToEmployers, setSettingsShowToEmployers] = useState<boolean | undefined>(undefined);
    const cvFileInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (!props.open) return undefined;

        if (!props.user?.userid) {
            setSettingsShowToEmployers(false);
            return undefined;
        }

        let cancelled = false;

        (async () => {
            try {
                const res = await getUserSettings({ userid: props.user?.userid }, false);
                if (!cancelled) {
                    if (!res.data.showToEmployers) setSettingsShowToEmployers(res.data.showToEmployers);
                }
            } catch {
                if (!cancelled) setSettingsShowToEmployers(undefined);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [props.open, props.user?.userid]);

    const defaultCV = props.userProfile?.cvs?.find(c => c.default)?.cvid
        ?? props.userProfile?.cvs?.[0]?.cvid;
    const hasExistingCvs = (props.userProfile?.cvs?.length ?? 0) > 0;
    const [selectedCvId, setSelectedCvId] = useState<number | null>(defaultCV ?? null);
    useEffect(() => {
        if (!selectedCvId) setSelectedCvId(defaultCV ?? null);
    }, [defaultCV]);
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<IFormRegister>({
        defaultValues: {
            ...emptyFormDefaults(),
            email: props.user?.email ?? props.userProfile?.email ?? '',
            firstname: props.userProfile?.firstname ?? '',
            lastname: props.userProfile?.lastname ?? '',
            phone: props.userProfile?.phone ?? '',
        },
    });
    useEffect(() => {
        if (!props.open) return;
        reset({
            ...emptyFormDefaults(),
            email: props.user?.email ?? props.userProfile?.email ?? '',
            firstname: props.userProfile?.firstname ?? '',
            lastname: props.userProfile?.lastname ?? '',
            phone: props.userProfile?.phone ?? '',
        });
    }, [
        props.open,
        props.job?.jobid,
        props.user?.userid,
        props.user?.email,
        props.userProfile?.userid,
        props.userProfile?.email,
        props.userProfile?.phone,
        props.userProfile?.firstname,
        props.userProfile?.lastname,
        reset,
    ]);
    const onSubmit = async (data: IFormRegister) => {
        try {
            window.showBackdrop();
            let { user } = props;
            if (!user) {
                const userRes = await registerUser({
                    smartAgentByActivity: data.smartAgentByActivity,
                    email: data.email,
                    password: data.password,
                    lastname: data.lastname,
                    firstname: data.firstname,
                    phone: data.phone,
                });
                user = userRes.data;
                props.onRegistrationComplete(user);
            }
            const userProfile = (
                await postUserProfile(
                    { userid: user.userid },
                    {
                        email: data.email,
                        firstname: data.firstname,
                        lastname: data.lastname,
                        phone: data.phone,
                    },
                )
            ).data;

            let cvidForApply: number | undefined;
            if (hasExistingCvs) {
                const cvs = props.userProfile?.cvs;
                cvidForApply = selectedCvId ?? defaultCV ?? cvs?.[0]?.cvid;
            } else if (data.cv?.length) {
                const formData = new FormData();
                formData.append('file', data.cv[0]);
                const cv = (await uploadUserCV({ userid: user.userid }, formData)).data;
                userProfile.cvs = [...(userProfile.cvs ?? []), cv];
                cvidForApply = cv.cvid;
            }

            props.onUserProfileUpdate(userProfile);
            await props.onApplyClick(userProfile, props.job, {
                message: data.message,
                cvid: cvidForApply,
            });
            props.onClose();
        } catch (err) {
            // report to Sentry
            Sentry.captureException(err);
            setFormErr(err.response?.data || err.message);
        } finally {
            window.hideBackdrop();
        }
    };

    const showLoginModal = React.useCallback(() => {
        props.onClose();
        window.modals.toggleLoginModal();
    }, []);

    const onAllowShowToEmployersChange = async (_: unknown, checked: boolean) => {
        const userid = props.user?.userid;
        if (!userid) return;
        setSettingsShowToEmployers(checked);
        try {
            await putUserSettings({ userid }, { showToEmployers: checked }, false);
        } catch {
            setSettingsShowToEmployers(!checked);
            window.showGlobalModal(
                'שמירת הגדרות',
                'לא ניתן היה לעדכן את נראות הפרופיל. נסו שוב או עדכנו בהגדרות המשתמש.',
            );
        }
    };

    const emailInput = register('email', {
        required: !(props.user?.email || props.userProfile?.email),
        ...((props.user?.email || props.userProfile?.email) ? {} : { pattern: emailPattern }),
    });
    const passwordInput = register('password', {
        required: !props.user,
        ...(!props.user ? { minLength: 6 } : {}),
    });
    const firstNameInput = register('firstname');
    const lastNameInput = register('lastname');
    const phoneInput = register('phone', { required: !props.userProfile?.phone });
    const cvInput = register('cv', {
        required: !!props.job?.cvMandatory && !hasExistingCvs,
    });
    const employerMessageInput = register('message');
    const smartAgentByActivityInput = register('smartAgentByActivity');

    return props.job ? (
        <DialogWithHeader
            open={props.open}
            onFirstActionClick={props.user ? props.onClose : showLoginModal}
            firstActionText={props.user ? 'ביטול' : 'התחברות'}
            secondActionText={
                props.user ? 'הגשת מועמדות' : 'הרשמה לאתר והגשת מועמדות'
            }
            onSecondActionClick={handleSubmit(onSubmit)}
            onClose={props.onClose}
            title="הגשת מועמדות"
        >
            <ApplyToJobMissingDataModalStyle>
                <CustomBR />
                {formErr && (
                    <>
                        <Alert severity="error">{formErr}</Alert>
                        <CustomBR />
                    </>
                )}
                <Typography display="inline">
          הינך עומד להגיש מועמדות למשרה:{' '}
                </Typography>
                <Anchor
                    href={`/job/details/${props.job.jobid}`}
                    fontWeight="bold"
                    text={props.job.title}
                    blank
                />
                <CustomBR />
                <Typography>על מנת להגיש מועמדות יש למלא את הפרטים הבאים</Typography>
                <CustomBR />
                <form onSubmit={handleSubmit(onSubmit)}>
                    {!props.user && (
                        <>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        required
                                        {...emailInput}
                                        inputRef={emailInput.ref}
                                        name="email"
                                        id="email"
                                        label="כתובת אימייל"
                                        type="email"
                                        fullWidth
                                        autoComplete="username"
                                        error={!!errors.email}
                                        helperText={errors.email && 'כתובת המייל אינה תקינה'}
                                        InputProps={{
                                            endAdornment: <Mail />,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        required
                                        {...passwordInput}
                                        inputRef={passwordInput.ref}
                                        name="password"
                                        id="password"
                                        label="בחר סיסמא"
                                        type="password"
                                        autoComplete="new-password"
                                        fullWidth
                                        error={!!errors.password}
                                        helperText={
                                            errors.password && 'הסיסמא אינה תקינה, לפחות 6 תווים'
                                        }
                                        InputProps={{
                                            endAdornment: <Lock />,
                                        }}
                                    />
                                </Grid>
                            </Grid>
                            <CustomBR />
                            <Divider />
                            <CustomBR />
                        </>
                    )}
                    {(!props.userProfile?.firstname || !props.userProfile?.lastname) && <><Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                {...firstNameInput}
                                inputRef={firstNameInput.ref}
                                name="firstname"
                                id="firstname"
                                label="שם פרטי"
                                defaultValue={props.userProfile?.firstname}
                                type="text"
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                {...lastNameInput}
                                inputRef={lastNameInput.ref}
                                name="lastname"
                                id="lastname"
                                label="שם משפחה"
                                defaultValue={props.userProfile?.lastname}
                                type="text"
                                fullWidth
                            />
                        </Grid>
                    </Grid><CustomBR /></>}
                    {!props.userProfile?.email && <><TextField
                        required
                        {...emailInput}
                        inputRef={emailInput.ref}
                        name="email"
                        id="email"
                        label="כתובת אימייל"
                        type="email"
                        fullWidth
                        autoComplete="username"
                        defaultValue={props.userProfile?.email}
                        error={!!errors.email}
                        helperText={errors.email && 'כתובת המייל אינה תקינה'}
                        InputProps={{
                            endAdornment: <Mail />,
                        }}
                    /><CustomBR /></>}
                    {!props.userProfile?.phone && <><TextField
                        required
                        {...phoneInput}
                        inputRef={phoneInput.ref}
                        name="phone"
                        id="phone"
                        label="מספר טלפון"
                        type="number"
                        helperText={errors.phone && 'חובה להזין טלפון'}
                        defaultValue={props.userProfile?.phone}
                        fullWidth
                        InputProps={{
                            endAdornment: <Phone />,
                        }}
                    /><CustomBR /></>}
                    {hasExistingCvs ? (
                        <>
                            <Box display="flex" alignContent="center">
                                <AttachFile color="primary" />
                                <Typography display="inline">קורות חיים להגשה&nbsp;</Typography>(
                                <Anchor text="ניהול קורות החיים שלי" blank href="/user/cvs" />)
                            </Box>
                            <CustomBR h={10} />
                            <Select
                                fullWidth
                                onChange={ev => {
                                    setSelectedCvId(parseInt(ev.target.value as string, 10));
                                }}
                                defaultValue={defaultCV}
                                size="small"
                                options={props.userProfile.cvs?.map(cvItem => ({
                                    text: cvItem.originalName,
                                    value: cvItem.cvid,
                                }))}
                            />
                            <CustomBR />
                        </>
                    ) : (
                        <Box>
                            <Typography>קורות חיים להגשה&nbsp;</Typography>
                            <TextField
                                required={!!props.job?.cvMandatory}
                                {...cvInput}
                                inputRef={el => {
                                    cvInput.ref(el);
                                    cvFileInputRef.current = el;
                                }}
                                name="cv"
                                id="cv"
                                type="file"
                                fullWidth
                                error={!!errors.cv}
                                helperText={errors.cv && 'חובה לצרף קורות חיים'}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                edge="end"
                                                aria-label="בחר קובץ קורות חיים"
                                                onClick={() => cvFileInputRef.current?.click()}
                                            >
                                                <AttachFile />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                    )}
                    <CustomBR h={10} />
                    <CustomBR />
                    <Box display="flex" alignContent="center">
                        <EmailOutlined color="primary" />
                        <Typography display="inline">
              &nbsp;לבחירתך, ניתן להוסיף מספר מילים שיישלחו אל המעסיק :
                        </Typography>
                    </Box>
                    <CustomBR h={10} />
                    <TextArea
                        {...employerMessageInput}
                        inputRef={employerMessageInput.ref}
                        name="message"
                        id="message"
                        label="הודעה קצרה למעסיק"
                        fullWidth
                        rows={2}
                    />
                    {settingsShowToEmployers !== undefined && <>
                        <CustomBR />
                        <FormGroup>
                            <FormControl component="fieldset" variant="standard">
                                <FormControlLabel
                                    control={(
                                        <Switch
                                            checked={settingsShowToEmployers}
                                            onChange={onAllowShowToEmployersChange}
                                            color="primary"
                                        />
                                    )}
                                    label={(
                                        <Box ml={1}>
                                            <Typography variant="body1" fontWeight={600} display="block">
                                                הפעל סוכן AI אישי שיחפש עבורי עבודה
                                            </Typography>
                                        </Box>
                                    )}
                                />
                                <FormHelperText>
                                    הסוכן ai שלנו ינתח את קורות החיים שלך ומידי יום ימליץ עליהם למעסיקים ומשרות בהם יש התאמה
                                </FormHelperText>
                            </FormControl>
                        </FormGroup>
                        <CustomBR />
                    </>}
                    {!props.user && <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    defaultChecked
                                    {...smartAgentByActivityInput}
                                    inputRef={smartAgentByActivityInput.ref}
                                    name="smartAgentByActivity"
                                />
                            }
                            label="הפעל סוכן חכם מבוסס פעילות"
                        />
                        <FormHelperText>
              קבלת התראות במייל על משרות חדשות מותאמות לפעילות שלכם
                        </FormHelperText>
                    </FormGroup>}
                </form>
            </ApplyToJobMissingDataModalStyle>
        </DialogWithHeader>
    ) : null;
};
export default ApplyToJobMissingDataModal;
